import { memo, useRef, useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useReactFlow, useStore } from '@xyflow/react';
import get from 'lodash/get';

import useTextWrapping from '../../../../components/SVGDrawer/useTextWrapping';
import classnames from 'src/util/classnames';
import WorkflowContext from '../../Workflow/WorkflowContext';
import { useDarkMode } from '../../../../context/DarkModeContext';

const selector = (s) => ({
  transform: s.transform,
  patternId: `pattern-${s.rfId}`,
  nodes: s.nodes,       
  paneWidth: s.width,
});

const GAP_PAD = 10;

function RoleBackground({ id, size, color, height, style, className, updateNodePositionsFromRoles }) {
  const ref = useRef(null);

  const { transform, patternId, paneWidth } = useStore(selector, shallow);
  const zoom = transform?.[2] || 1;

  const { getNodes, setNodes } = useReactFlow();
  const { state, dispatchAction } = useContext(WorkflowContext);

  const roles = useMemo(() => state.roles || [], [state]);
  const layout = useMemo(() => state.layout || {}, [state]);

  const _patternId = `${patternId}${id ? id : ''}`;
  const scaledGap = [20 * zoom, 20 * zoom];
  const scaledOffset = [1 + scaledGap[0] / 2, 1 + scaledGap[1] / 2];
  const scaledSize = size * zoom;

  const [heights, setHeights] = useState(
    roles.map((role) => get(layout, ['swimmingLane', 'roleHeights', role]) ?? 300)
  );
  const [width, setWidth] = useState(get(layout, ['swimmingLane', 'width']) ?? 1000);

  // Los heights  estaba siendo seteados una sola vez en el montaje
  // pero si subes un workflow que ya tiene alturas guardadas el roleheight
  // no se actualiza. Ahora se actualizan cada vez que cambian los roles o el layout
  // Sin esto el height es siempre 200 a menos que recargues la página
  useEffect(() => {
    setHeights(roles.map((role) => get(layout, ['swimmingLane', 'roleHeights', role]) ?? 300));
  }, [roles, layout?.swimmingLane?.roleHeights]);

  useEffect(() => {
    setWidth(get(layout, ['swimmingLane', 'width']) ?? 1000);
  }, [layout?.swimmingLane?.width]);

  const updateLaneHeight = (idx, newHeight) => {
    setHeights((prev) => {
      const next = [...prev];
      next[idx] = Math.max(200, newHeight);
      return next;
    });
  };

  // Suma todas las alturas de los lanes para saber cuabnto mide el diagrama verticalmente y luego forzar un resize
  const totalHeight = heights.reduce((a, b) => a + b, 0);
  // Aqui evitamos que el usuario tenga que hacer zoom o arrastrar el canvas para que se actualice
  useEffect(() => {
    const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    return () => clearTimeout(t);
  }, [totalHeight, width]);

  // --- NEW: persist note positions after we move them ---
  const persistNotesFromNodes = useCallback(
    (nodesLike) => {
      const notesOnly = nodesLike
        .filter((n) => n.type === 'note')
        .map((n) => ({
          id: n.id,
          x: n.position.x,
          y: n.position.y,
          text: n.data?.text || '',
        }));
      dispatchAction.setWorkflowNotes({ notes: notesOnly });
    },
    [dispatchAction]
  );

  const shiftNodesBelowLane = useCallback(
    (cutoffY, deltaY) => {
      if (!deltaY) return;
  
      const before = getNodes();
      const after = before.map((node) => {
        const top = node?.position?.y ?? 0;
  
        // estimate height per type
        const estHeight =
          node?.height ??
          (node.type === 'note'
            ? 110 // default note height
            : node?.data?.stepdef?.size?.height ?? 60);
  
        const bottom = top + estHeight;
  
        // move if the node is below OR intersects the cutoff line
        // since you can add notes anywhere they can go on top of a lane dividing line
        // so they need to move also down when the line is dragged
        if (bottom >= cutoffY - 0.5) {
          const newY = top + deltaY;
  
          // persist step positions; notes are persisted later
          const isStep = !!state?.steps?.[node.id];
          if (isStep) {
            dispatchAction.setStepPosition({
              name: node.id,
              position: { x: node.position.x, y: newY },
            });
          }
  
            return {
              ...node,
              position: { ...node.position, y: newY },
            };
        }
  
        return node;
      });
  
      setNodes(after);
  
      // persist *notes* back into workflowLayout
      const notesOnly = after
        .filter((n) => n.type === 'note')
        .map((n) => ({
          id: n.id,
          x: n.position.x,
          y: n.position.y,
          text: n.data?.text || '',
        }));
      dispatchAction.setWorkflowNotes({ notes: notesOnly });
    },
    [getNodes, setNodes, dispatchAction, state?.steps]
  );   

  // inner width lanes should draw to (shrinkable; allow notes to overhang)
  const laneInnerWidth = useMemo(() => {
    const gap = layout?.swimmingLane?.gap ?? 0;
    return Math.max((width ?? 1000) - gap, 600); // minimum inner width
  }, [width, layout?.swimmingLane?.gap]);

  return (
    <>
      {/* Wrapper sincronizado con el viewport de React Flow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0, // keep behind nodes so notes are draggable anywhere
        }}
      >
        <div
          style={{
            transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${zoom})`,
            transformOrigin: '0 0',
            width: '0', // evita que cree scrolls raros en algunos navegadores
            height: '0',
          }}
        >
          <svg
            className={classnames('workflow react-flow__background', className)}
            style={{
              ...style,
              display: 'block',
              overflow: 'visible',
            }}
            width={width - layout.swimmingLane.gap}
            height={totalHeight}
            ref={ref}
            data-testid="rf__background"
          >
            <pattern
              id={_patternId}
              x={transform[0] % scaledGap[0]}
              y={transform[1] % scaledGap[1]}
              width={scaledGap[0]}
              height={scaledGap[1]}
              patternUnits="userSpaceOnUse"
              patternTransform={`translate(-${scaledOffset[0]},-${scaledOffset[1]})`}
            >
              <DotPattern radius={scaledSize / 2} />
            </pattern>

            <g>
              {roles.map((role, idx) => (
                <RoleSVG
                  key={idx}
                  idx={idx}
                  ///no usamos scale interno; el wrapper aplica pan/zoom
                  role={role}
                  layout={layout}
                  heights={heights}
                  width={laneInnerWidth}
                  dispatchAction={dispatchAction}
                />
              ))}
            </g>

            <rect x="0" y="0" width="100%" height="100%" fill={`url(#${_patternId})`} />
          </svg>
        </div>
      </div>

      {roles.map((role, idx) => {
        if (idx === roles.length) return null;

        // lane bottom Y in FLOW coords
        const laneBottomFlow = heights.slice(0, idx + 1).reduce((a, b) => a + b, 0);
        // convert to SCREEN coords for the handle positioning
        const top = laneBottomFlow * zoom + transform[1] - 4;

        return (
          <div
            key={`drag-handler-${idx}`}
            onMouseDown={(e) => {
              e.preventDefault();
              const startY = e.clientY;
              const originalHeight = heights[idx];

              const onMouseMove = (moveEvent) => {
                const deltaY = (moveEvent.clientY - startY) / zoom;
                updateLaneHeight(idx, originalHeight + deltaY);
              };

              const onMouseUp = (upEvent) => {
                const rawDelta = (upEvent.clientY - startY) / zoom;
                const unclampedHeight = originalHeight + rawDelta;
                const clampedHeight = Math.max(200, unclampedHeight);
                const clampedDelta = clampedHeight - originalHeight;

                updateLaneHeight(idx, clampedHeight);
                dispatchAction.setSwimmingLaneHeight({ roleName: role, height: clampedHeight });

                // --- KEY: shift all nodes (steps + notes) at/below this lane’s bottom
                shiftNodesBelowLane(laneBottomFlow, clampedDelta);

                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                document.body.style.cursor = '';
              };

              document.addEventListener('mousemove', onMouseMove);
              document.addEventListener('mouseup', onMouseUp);
              document.body.style.cursor = 'ns-resize';
            }}
            style={{
              position: 'absolute',
              top: `${top}px`,
              left: `${transform[0]}px`,
              height: '8px',
              width: `${laneInnerWidth * zoom}px`,
              cursor: 'ns-resize',
              zIndex: 2001,
              opacity: 0.6,
              pointerEvents: 'auto',
            }}
          />
        );
      })}

      {/* Right edge width handle */}
      <div
        style={{
          position: 'absolute',
          top: transform[1],
          left: `${laneInnerWidth * zoom + transform[0]}px`,
          height: `${totalHeight * zoom}px`,
          width: '10px',
          cursor: 'ew-resize',
          zIndex: 10,
          opacity: 0.6,
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          const startX = e.clientX;
          const originalWidth = width;

          const onMouseMove = (moveEvent) => {
            const deltaX = (moveEvent.clientX - startX) / zoom;
            setWidth(originalWidth + deltaX);
          };

          const onMouseUp = (upEvent) => {
            const rawDelta = (upEvent.clientX - startX) / zoom;
            const unclampedWidth = originalWidth + rawDelta;
            const clampedWidth = Math.max(600, unclampedWidth);

            setWidth(clampedWidth);
            dispatchAction.setSwimmingLaneWidth({ width: clampedWidth });

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
          };

          document.addEventListener('mousemove', onMouseMove);
          document.addEventListener('mouseup', onMouseUp);
          document.body.style.cursor = 'ew-resize';
        }}
      />
    </>
  );
}

RoleBackground.displayName = 'Background';

function RoleSVG({ idx, role, layout, heights, width, dispatchAction }) {
  const ref = useRef();
  const { isDarkMode } = useDarkMode();

  const y = heights.slice(0, idx).reduce((sum, h) => sum + h, 0);
  const gap = layout.swimmingLane.gap;
  const height = heights[idx];
  const minLines = layout.swimmingLane.maxLines;

  const lines = useTextWrapping({ content: role, ref, maxWidth: height * 0.83 });
  const _lineCount = lines?.length || 1;

  useEffect(() => {
    if (_lineCount) {
      dispatchAction.setSwimmingLaneLineCount({ idx, lineCount: _lineCount });
    }
  }, [_lineCount, idx, height, dispatchAction]);

  const lineCount = minLines > _lineCount ? minLines : _lineCount;
  const gap_pad = GAP_PAD + ((1 - _lineCount / lineCount) * gap) / 2;

  ///Now, laneInnerWidth (the computed width passed into <RoleSVG>) already accounts for "gap" so I removed gap
  return (
    <g transform={`translate(0, ${y | 0})`}>
      <rect width={`${width}px`} height={`${height}px`} stroke={isDarkMode ? '#40444f' : '#000'} fillOpacity={0} />
      <g>
        <g transform={`translate(${gap_pad}, ${(height / 2) | 0})`}>
          <text
            ref={ref}
            y=".9em"
            alignmentBaseline="top"
            textAnchor="middle"
            transform="rotate(-90)"
            fill={isDarkMode ? '#e2e4e9' : '#000000'}
          >
            {(lines || []).map((line, i) => (
              <tspan key={i} x="0" dy={i ? '1.2em' : ''}>
                {line}
              </tspan>
            ))}
          </text>
        </g>
      </g>
    </g>
  );
}

function DotPattern({ radius, className }) {
  return (
    <circle
      cx={radius}
      cy={radius}
      r={radius}
      className={classnames(['react-flow__background-pattern', 'dots', className])}
    />
  );
}

export default memo(RoleBackground);
